import {
  Badge,
  Column,
  Columns,
  EmptyState,
  FlexBox,
  Text,
} from '@dixa/lib-shared-habla';
import type { SearchParameters } from 'lib-search-types';
import { ampli } from 'lib-shared-tracking';
import { ReactNode, useCallback, useEffect, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { Writable } from 'ts-essentials';

import { searchApi } from 'apis/search';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { useAppNavigate } from 'utils/url/useAppNavigate';
import { useAppPathParams } from 'utils/url/useAppPathParams';

import { SearchFilters } from './components/filters/searchFilters';
import { SearchFiltersProvider } from './components/filters/searchFilters.context';
import { SearchInput } from './components/input/searchInput';
import { SearchResultsLoading } from './components/results/components/loading/searchResultsLoading';
import { SearchResults } from './components/results/searchResults';
import { SavedSearchButton } from './components/savedSearch/button/savedSearchButton';
import { SavedSearchFormModal } from './components/savedSearch/form/savedSearchFormModal';
import { SavedSearchListModal } from './components/savedSearch/list/savedSearchListModal';
import { searchActions } from './search.actions';
import styles from './search.view.module.scss';
import { maxResultsCount } from './utils/results/maxResultsCount';
import { createSearchUrlParams } from './utils/url/createSearchUrl';
import { useSearchInit } from './utils/useSearchInit/useSearchInit';

export const SearchView = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const navigate = useAppNavigate();

  const { isInitialized } = useSearchInit();
  const { scope } = useAppPathParams('/search/:scope');
  const { params } = useAppSelector((state) => state.search);

  const [readSearchResultsQuery, { data, isLoading, isError }] =
    searchApi.useLazyReadAdvancedSearchResultsQuery();

  const readSearchResultsQueryResultRef =
    useRef<ReturnType<typeof readSearchResultsQuery>>();

  const readSearchResults = useCallback(() => {
    if (readSearchResultsQueryResultRef.current) {
      readSearchResultsQueryResultRef.current.abort();
    }

    readSearchResultsQueryResultRef.current = readSearchResultsQuery({
      ...params,
      scope,
      enrichmentSpec: 'ALL',
    } as SearchParameters<'conversations', true>);
  }, [params, readSearchResultsQuery, scope]);

  useEffect(() => {
    if (isInitialized) {
      readSearchResults();
    }
  }, [isInitialized, readSearchResults]);

  const updateSearchParams = useCallback(
    (newParams: Partial<SearchParameters<'conversations'>>) => {
      dispatch(searchActions.params.update({ ...newParams, page: 1 }));
    },
    [dispatch],
  );

  const handleSubmitInput = useCallback(
    (query: string, isStrict: boolean) => {
      const nextParams: Partial<Writable<SearchParameters<'conversations'>>> = {
        query,
        isStrict,
      };

      if (!query) {
        nextParams.isStrict = false;
      }
      updateSearchParams(nextParams);

      ampli.searchFieldSubmitted({
        location: 'searchPage',
        isEmpty: !query,
        query,
      });
    },
    [updateSearchParams],
  );

  const handleClearInput = useCallback(() => {
    updateSearchParams({ query: '', isStrict: false });

    if (!params.filters.filters.length) {
      dispatch(searchActions.savedSearch.close());
    }

    ampli.searchFieldCleared({ location: 'searchPage' });
  }, [dispatch, params.filters.filters.length, updateSearchParams]);

  const handleClearSavedSearch = useCallback(() => {
    dispatch(searchActions.savedSearch.close());

    navigate({
      path: '/search/:scope',
      params: createSearchUrlParams({ scope: 'conversations' }),
    });

    ampli.savedSearchClosed();
  }, [dispatch, navigate]);

  if (scope !== 'conversations') {
    return <Navigate to="/search/conversations" replace />;
  }

  const totalResults = data?.meta.total_results;

  const showTotalResults =
    typeof totalResults === 'number' &&
    (!!params.query || params.filters.filters.length > 0);

  const renderContent = (): ReactNode => {
    if (!isInitialized || isLoading) {
      return <SearchResultsLoading />;
    }

    if (isError) {
      return (
        <FlexBox
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          height="100%"
        >
          <EmptyState
            title="Something went wrong"
            description="We're experiencing some issues, try again or reload the page. If this issue persists, please contact support."
            icon="exclamation"
            iconColor="red.500"
            buttonProps={{
              children: 'Try again',
              onClick: readSearchResults,
            }}
          />
        </FlexBox>
      );
    }

    return <SearchResults />;
  };

  return (
    <Columns>
      <Column>
        <Column.Row className={styles.inputRow} noMinHeight>
          <SearchInput
            onSearch={handleSubmitInput}
            onClear={handleClearInput}
            onSavedSearchClear={handleClearSavedSearch}
          />
        </Column.Row>

        <Column.Row
          slottable
          borderBottom
          noMinHeight
          noVerticalPadding
          className={styles.titleRow}
        >
          <Column.Slot placement="start" className={styles.titleSlot}>
            <FlexBox alignItems="center" gap="200">
              <Text.Paragraph as="span" variant="1" subdued>
                Conversations
              </Text.Paragraph>

              {showTotalResults && (
                <Badge variant="secondary" size="small">
                  {Math.min(totalResults, maxResultsCount)
                    .toLocaleString(navigator.language)
                    .concat(totalResults >= maxResultsCount ? '+' : '')}
                </Badge>
              )}
            </FlexBox>
          </Column.Slot>

          <Column.Slot placement="end">
            <FlexBox gap="200">
              <SearchFiltersProvider>
                <SearchFilters />
              </SearchFiltersProvider>

              <SavedSearchButton
                buttonProps={{
                  variant: 'secondary',
                }}
              />
            </FlexBox>
          </Column.Slot>
        </Column.Row>

        <Column.Row size={1} noVerticalPadding noHorizontalPadding>
          {renderContent()}
        </Column.Row>

        <SavedSearchFormModal />
        <SavedSearchListModal />
      </Column>
    </Columns>
  );
};
